import { HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../auth/services/auth/auth.service';
import { catchError, switchMap } from 'rxjs';

/** Rutas de auth que nunca deben disparar una renovación (evita bucles). */
const RUTAS_SIN_REFRESH = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh-token',
  '/auth/exchange-google-code',
  '/auth/activate-account',
  '/auth/resend-activation',
  '/auth/google',
];

/** Indica si la petición es del propio flujo de autenticación. */
function esRutaAuth(req: HttpRequest<unknown>): boolean {
  return RUTAS_SIN_REFRESH.some((ruta) => req.url.includes(ruta));
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authService = inject(AuthService);
  const token = authService.token();

  // Sin token no se añade cabecera (antes se enviaba `Bearer null`)
  const conAuth = token
    ? req.clone({
        withCredentials: true,
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      })
    : req.clone({ withCredentials: true });

  return next(conAuth).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !esRutaAuth(req)) {
        // El refresh es singleflight en AuthService: las peticiones
        // concurrentes comparten la misma renovación y reintentan una vez
        return authService.refreshToken().pipe(
          switchMap((success) => {
            if (success) {
              const refreshedToken = authService.token();
              const retryReq = refreshedToken
                ? req.clone({
                    withCredentials: true,
                    headers: req.headers.set('Authorization', `Bearer ${refreshedToken}`),
                  })
                : req.clone({ withCredentials: true });
              return next(retryReq);
            } else {
              // Refresh inválido o ausente: se expulsa guardando la ruta
              // actual para volver tras el login (ver AuthService.logout)
              authService.logout();
              throw error;
            }
          }),
        );
      }
      throw error;
    }),
  );
}
